import React, { forwardRef, useEffect, useLayoutEffect, useRef } from 'react'
import './TextArea.sass'

type Props = {
  className?: string
  value: string
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  placeholder?: string
  minRows?: number
  maxRows?: number
  disabled?: boolean
  readOnly?: boolean
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void
  style?: React.CSSProperties
  onKeyPress?: React.KeyboardEventHandler<HTMLTextAreaElement>
  /** Подсказка ОС/браузеру для клавиатуры (например `decimal` на мобилке для чисел) */
  inputMode?: React.HTMLAttributes<HTMLTextAreaElement>['inputMode']
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n))
}

const TextArea = forwardRef<HTMLTextAreaElement, Props>(function TextArea ({
  className = '',
  value,
  onChange,
  placeholder,
  minRows,
  maxRows,
  disabled = false,
  readOnly = false,
  onFocus,
  onBlur,
  style,
  onKeyPress,
  inputMode,
}, ref) {
  const innerRef = useRef<HTMLTextAreaElement | null>(null)

  const resolvedMaxRows = maxRows ?? 1
  // Если `minRows` не передали, логично считать, что оно равно `maxRows`.
  // Это сохраняет поведение “однострочного поля”, когда в коде передавали только `maxRows`.
  const resolvedMinRows = minRows ?? resolvedMaxRows

  // Наш autosize вычисляет min <= max, поэтому нормализуем на случай неверных комбинаций.
  const safeMinRows = Math.min(resolvedMinRows, resolvedMaxRows)
  const safeMaxRows = Math.max(resolvedMinRows, resolvedMaxRows)

  const setRefs = (el: HTMLTextAreaElement | null) => {
    innerRef.current = el
    if (typeof ref === 'function') ref(el)
    else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = el
  }

  const autosize = () => {
    const el = innerRef.current
    if (!el) return

    // reset so shrink works
    el.style.height = 'auto'

    const computed = window.getComputedStyle(el)
    const lineHeight = parseFloat(computed.lineHeight || '0') || 22
    const paddingTop = parseFloat(computed.paddingTop || '0') || 0
    const paddingBottom = parseFloat(computed.paddingBottom || '0') || 0

    const minH = lineHeight * safeMinRows + paddingTop + paddingBottom
    const maxH = lineHeight * safeMaxRows + paddingTop + paddingBottom
    const nextH = clamp(el.scrollHeight, minH, maxH)
    // Округляем до целых пикселей, чтобы избежать визуальных артефактов
    // (line-height задан в процентах -> может давать дробные значения).
    el.style.height = `${Math.round(nextH)}px`
    el.style.overflowY = el.scrollHeight > nextH ? 'auto' : 'hidden'
  }

  useLayoutEffect(() => {
    autosize()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    autosize()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, minRows, maxRows])

  return (
    <div className={`custom-text-area ui-kit-v2 ${disabled ? 'is-disabled' : ''} ${className}`.trim()}>
      <textarea
        ref={setRefs}
        className="custom-text-area__input"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={safeMinRows}
        disabled={disabled}
        readOnly={readOnly}
        onFocus={onFocus}
        onBlur={onBlur}
        style={style}
        onKeyPress={onKeyPress}
        inputMode={inputMode}
      />
    </div>
  )
})

export default TextArea
